import { Badge, Button, cn } from '@provenance/design';
import {
  PAGE_KIND_LABEL,
  markFontFor,
  primaryTemplatePage,
  type PageKind,
  type StampSlot,
  type TemplatePageKinds,
} from '@provenance/shared';
import { Loader2, Minus, Plus, Upload } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  boxToScreen,
  movedSlot,
  resizedSlot,
  type MarkLayout,
  type PageGeometry,
  type ResizeHandle,
} from './coordinates';
import { usePdfPage } from './usePdfPage';

/** The tag on a page button: short, because the buttons are. */
const KIND_SHORT: Record<PageKind, string> = { CERTIFICATE: 'Face', CONTINUATION: 'Cont.', OVERLEAF: 'Notes' };

/**
 * ════════════════════════════════════════════════════════════════════════════
 * THE VISUAL STAMP PLACEMENT CANVAS (spec §7.6.2, T-125, T-126)
 *
 * The template is rendered by pdf.js onto a canvas, and marks are dragged on top of it.
 * Every stored value is captured from where the mark was dropped or sized — an
 * administrator never types a coordinate unless they want to.
 *
 * WHAT YOU PLACE IS WHAT PRINTS. Each mark is drawn at the box `@provenance/shared`
 * computes — the same function the stamping engine draws with — from the image's real
 * pixel size and the engine's own font metrics. The canvas used to guess (an image's
 * height as width × 0.3), and a seal placed at 150 × 45 printed at 150 × 124.
 *
 * STACKING IS THE LAYER ORDER. Marks are drawn back to front in list order, exactly as
 * the engine draws them. Selecting a mark outlines it on a layer above everything, but
 * never brings it forward — the canvas used to, so it could not show what was on top.
 *
 * PRECISION. Drag is coarse, so arrow keys nudge by 1pt (10 with Shift), and `]` / `[`
 * move the selected mark forward or back a layer.
 * ════════════════════════════════════════════════════════════════════════════
 */

const ZOOMS = [0.75, 1, 1.25, 1.5, 2] as const;
/** The canvas' unzoomed CSS width. A4 at this width is a comfortable working size. */
const BASE_WIDTH = 720;
/** Fallback page size when a chamber has not uploaded the blank form. */
const A4_WIDTH = 595.28;
const A4_HEIGHT = 841.89;

type Drag =
  | { kind: 'move'; id: string; startX: number; startY: number; slot: StampSlot; layout: MarkLayout }
  | { kind: 'resize'; id: string; handle: ResizeHandle; slot: StampSlot; layout: MarkLayout };

export function StampCanvas({
  templateUrl,
  compareUrl = null,
  pageWidth,
  pageHeight,
  slots,
  layouts,
  selectedId,
  onSelect,
  onChange,
  onLayer,
  onPageSize,
  coveredIds,
  page,
  onPageChange,
  onUploadTemplate,
  pageKinds,
  onPageKindChange,
  stampOverleaf,
  onStampOverleafChange,
  onCopyFromFace,
}: {
  templateUrl: string | null;
  /**
   * The current proof, when comparing. The canvas then renders the ENGINE'S output as the
   * page and draws each mark as an outline only, so any disagreement shows as an outline
   * that does not sit on its mark.
   */
  compareUrl?: string | null;
  /** The page size the saved coordinates were measured against, when no template is held. */
  pageWidth: number | null;
  pageHeight: number | null;
  /** Back to front: index 0 is drawn first and sits behind everything. */
  slots: StampSlot[];
  layouts: Record<string, MarkLayout>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChange: (id: string, patch: Partial<StampSlot>) => void;
  onLayer: (id: string, direction: 'forward' | 'back') => void;
  /** The rendered page's true size, so layouts clamp against the page actually shown. */
  onPageSize: (size: { width: number; height: number }) => void;
  /** Marks hidden by something in front of them. */
  coveredIds: Set<string>;
  page: number;
  onPageChange: (page: number) => void;
  onUploadTemplate: () => void;
  /** What each template page is (T-137): certificate face, continuation, overleaf, or untagged. */
  pageKinds: TemplatePageKinds;
  onPageKindChange: (page: number, kind: PageKind | null) => void;
  /** Overleaf pages may carry marks (D-028: never by default). */
  stampOverleaf: boolean;
  onStampOverleafChange: (allowed: boolean) => void;
  /** Copies the certificate face's marks onto this (continuation) page, ready to adjust. */
  onCopyFromFace: (page: number) => void;
}) {
  const [zoom, setZoom] = useState<number>(1);
  const holderRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);

  const background = compareUrl ?? templateUrl;
  const rendered = usePdfPage(background, page, BASE_WIDTH * zoom);

  // Without a template we still place against a page, because a chamber's coordinates are
  // measured in PDF points regardless of whether we hold the blank form.
  const blank = !background;
  const pointWidth = blank ? (pageWidth ?? A4_WIDTH) : rendered.pointWidth;
  const pointHeight = blank ? (pageHeight ?? A4_HEIGHT) : rendered.pointHeight;
  /**
   * Memoised deliberately: a fresh object here would tear down and re-attach the pointer
   * listeners below on every render — most often mid-drag, when a dropped event shows most.
   */
  const geometry: PageGeometry = useMemo(
    () => ({
      pointWidth,
      pointHeight,
      scale: blank ? (BASE_WIDTH * zoom) / pointWidth : rendered.scale,
    }),
    [pointWidth, pointHeight, blank, zoom, rendered.scale],
  );

  useEffect(() => {
    onPageSize({ width: pointWidth, height: pointHeight });
  }, [onPageSize, pointWidth, pointHeight]);

  const pageCount = Math.max(1, rendered.pageCount, ...slots.map((slot) => slot.page));
  const busy = !blank && rendered.loading;

  // The canvas is created imperatively by pdf.js, so it is attached rather than rendered.
  useLayoutEffect(() => {
    const holder = holderRef.current;
    if (!holder) return;
    holder.replaceChildren();
    if (rendered.canvas) holder.appendChild(rendered.canvas);
  }, [rendered.canvas]);

  /** A pointer position → PDF points on the page. */
  const toPdf = useCallback(
    (clientX: number, clientY: number) => {
      const rect = surfaceRef.current?.getBoundingClientRect();
      if (!rect) return null;
      return {
        x: (clientX - rect.left) / geometry.scale,
        y: geometry.pointHeight - (clientY - rect.top) / geometry.scale,
      };
    },
    [geometry],
  );

  const pageBox = useMemo(() => ({ width: pointWidth, height: pointHeight }), [pointWidth, pointHeight]);

  const beginMove = (event: React.PointerEvent, slot: StampSlot) => {
    // Stops text selection mid-drag — and with it the focus a click would give, which the
    // arrow-key nudge needs, so focus is put back by hand.
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).focus();
    const id = slot.id ?? '';
    onSelect(id);
    const layout = layouts[id];
    if (!layout) return;
    drag.current = { kind: 'move', id, startX: event.clientX, startY: event.clientY, slot, layout };
  };

  const beginResize = (event: React.PointerEvent, slot: StampSlot, handle: ResizeHandle) => {
    event.preventDefault();
    event.stopPropagation();
    const layout = layouts[slot.id ?? ''];
    if (!layout) return;
    drag.current = { kind: 'resize', id: slot.id ?? '', handle, slot, layout };
  };

  useEffect(() => {
    const onMoveEvent = (event: PointerEvent) => {
      const current = drag.current;
      if (!current) return;
      if (current.kind === 'move') {
        // From the snapshot taken at pointer-down, not the live slot: accumulating small
        // deltas onto a value that is itself being rounded drifts.
        const dx = (event.clientX - current.startX) / geometry.scale;
        const dy = -(event.clientY - current.startY) / geometry.scale;
        onChange(current.id, movedSlot(current.slot, current.layout, dx, dy, pageBox));
      } else {
        const point = toPdf(event.clientX, event.clientY);
        if (point) onChange(current.id, resizedSlot(current.slot, current.layout, current.handle, point));
      }
    };
    const onUp = () => {
      drag.current = null;
    };
    window.addEventListener('pointermove', onMoveEvent);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMoveEvent);
      window.removeEventListener('pointerup', onUp);
    };
  }, [geometry, onChange, pageBox, toPdf]);

  const onKeyDown = (event: React.KeyboardEvent, slot: StampSlot) => {
    const id = slot.id ?? '';
    if (event.key === ']' || event.key === '[') {
      event.preventDefault();
      onLayer(id, event.key === ']' ? 'forward' : 'back');
      return;
    }
    if (!event.key.startsWith('Arrow')) return;
    event.preventDefault();
    const layout = layouts[id];
    if (!layout) return;
    // 1pt precision is the point of this: a seal three points low is three key presses.
    const step = event.shiftKey ? 10 : 1;
    const dx = event.key === 'ArrowRight' ? step : event.key === 'ArrowLeft' ? -step : 0;
    const dy = event.key === 'ArrowUp' ? step : event.key === 'ArrowDown' ? -step : 0;
    onChange(id, movedSlot(slot, layout, dx, dy, pageBox));
  };

  const onPage = slots.filter((slot) => slot.page === page);
  const selected = onPage.find((slot) => slot.id === selectedId) ?? null;
  const selectedLayout = selected ? layouts[selected.id ?? ''] : undefined;
  const kindOf = (n: number): PageKind | null => pageKinds[n - 1] ?? null;
  const thisKind = kindOf(page);
  const byKind = pageKinds.includes('CERTIFICATE');
  const isOverleaf = thisKind === 'OVERLEAF' && !stampOverleaf;
  // A page that repeats another page's marks: the second copy of the certificate face, a
  // second continuation sheet. Its marks are edited on the first page of its kind.
  const primary = thisKind && byKind ? primaryTemplatePage(thisKind, pageKinds) : null;
  const repeats = primary !== null && primary !== page ? primary : null;
  const facePage = byKind ? primaryTemplatePage('CERTIFICATE', pageKinds) : null;
  const canCopyFace = thisKind === 'CONTINUATION' && repeats === null && facePage !== null && slots.some((s) => s.page === facePage);
  const [confirmCopy, setConfirmCopy] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {pageCount > 1 && (
          <div className="flex items-center gap-1">
            <span className="[font-size:var(--pv-text-xs)]  text-[var(--pv-faint)]">
              Page
            </span>
            {Array.from({ length: pageCount }, (_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => onPageChange(index + 1)}
                aria-current={page === index + 1}
                aria-label={`Page ${index + 1}${kindOf(index + 1) ? `, ${PAGE_KIND_LABEL[kindOf(index + 1)!].toLowerCase()}` : ''}`}
                title={kindOf(index + 1) ? PAGE_KIND_LABEL[kindOf(index + 1)!] : 'Not tagged'}
                className={cn(
                  'flex h-7 min-w-7 items-center gap-1 rounded-[var(--pv-radius-xs)] px-1.5 [font-size:12px] font-semibold',
                  page === index + 1
                    ? 'bg-[var(--pv-brand)] text-[var(--pv-brand-on)]'
                    : 'bg-[var(--pv-card-alt)] text-[var(--pv-muted)] hover:text-[var(--pv-ink)]',
                  kindOf(index + 1) === 'OVERLEAF' && !stampOverleaf && page !== index + 1 && 'line-through opacity-60',
                )}
              >
                {index + 1}
                {kindOf(index + 1) && (
                  <span aria-hidden className="[font-size:10px] font-medium opacity-80">
                    {KIND_SHORT[kindOf(index + 1)!]}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <label className="flex items-center gap-2 [font-size:12px] text-[var(--pv-muted)]">
          Page {page} is
          <select
            value={thisKind ?? ''}
            onChange={(e) => onPageKindChange(page, (e.target.value || null) as PageKind | null)}
            className="h-7 rounded-[var(--pv-radius-xs)] border border-[var(--pv-line)] bg-[var(--pv-card)] px-1.5 [font-size:12px] text-[var(--pv-ink)]"
          >
            <option value="">not tagged</option>
            <option value="CERTIFICATE">the certificate face</option>
            <option value="CONTINUATION">a continuation sheet</option>
            <option value="OVERLEAF">overleaf notes</option>
          </select>
        </label>

        {thisKind === 'OVERLEAF' && (
          <label className="flex cursor-pointer items-center gap-2 [font-size:12px] text-[var(--pv-muted)]">
            <input
              type="checkbox"
              checked={stampOverleaf}
              onChange={(e) => onStampOverleafChange(e.target.checked)}
              className="size-3.5 accent-[var(--pv-brand)]"
            />
            Allow marks on overleaf pages
          </label>
        )}

        {canCopyFace && (
          <Button
            size="sm"
            variant={confirmCopy ? 'primary' : 'secondary'}
            onClick={() => {
              if (onPage.length > 0 && !confirmCopy) {
                setConfirmCopy(true);
                return;
              }
              setConfirmCopy(false);
              onCopyFromFace(page);
            }}
            onBlur={() => setConfirmCopy(false)}
          >
            {confirmCopy
              ? `Replace the ${onPage.length} mark${onPage.length === 1 ? '' : 's'} here?`
              : `Copy marks from the certificate face (page ${facePage})`}
          </Button>
        )}

        {compareUrl && (
          <Badge tone="info" size="sm">
            Comparing: the page is the engine's proof, outlines are the editor
          </Badge>
        )}

        <div className="flex-1" />

        <span className="[font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
          {Math.round(pointWidth)} × {Math.round(pointHeight)} pt
          {blank && ' · no template'}
        </span>

        <div className="flex items-center gap-1 rounded-[var(--pv-radius-sm)] border border-[var(--pv-line-strong)] bg-[var(--pv-card)] px-1 py-0.5">
          <button
            type="button"
            onClick={() => setZoom((z) => ZOOMS[Math.max(0, ZOOMS.indexOf(z as never) - 1)] ?? z)}
            aria-label="Zoom out"
            className="rounded p-1 text-[var(--pv-muted)] hover:text-[var(--pv-ink)]"
          >
            <Minus aria-hidden className="size-3.5" />
          </button>
          <span className="pv-num min-w-[3rem] text-center [font-size:12px] font-medium">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() =>
              setZoom((z) => ZOOMS[Math.min(ZOOMS.length - 1, ZOOMS.indexOf(z as never) + 1)] ?? z)
            }
            aria-label="Zoom in"
            className="rounded p-1 text-[var(--pv-muted)] hover:text-[var(--pv-ink)]"
          >
            <Plus aria-hidden className="size-3.5" />
          </button>
        </div>
      </div>

      <div className="overflow-auto rounded-[var(--pv-radius-lg)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] p-4">
        <div
          ref={surfaceRef}
          className="relative mx-auto w-fit shadow-[var(--pv-shadow)]"
          onClick={() => onSelect(null)}
        >
          <div ref={holderRef} aria-hidden />

          {blank && (
            <div
              aria-hidden
              className="bg-[var(--pv-card)] bg-[linear-gradient(var(--pv-line)_1px,transparent_1px),linear-gradient(90deg,var(--pv-line)_1px,transparent_1px)] [background-size:var(--pv-grid)_var(--pv-grid)]"
              style={{
                width: pointWidth * geometry.scale,
                height: pointHeight * geometry.scale,
                // A 1cm grid, so an administrator can still judge distances by eye.
                ['--pv-grid' as string]: `${(28.35 * geometry.scale).toFixed(2)}px`,
              }}
            />
          )}

          {busy && (
            <div className="absolute inset-0 z-[60] grid place-items-center bg-[var(--pv-card)]/70">
              <span className="flex items-center gap-2 [font-size:13px] text-[var(--pv-muted)]">
                <Loader2 aria-hidden className="size-4 animate-spin" />
                {compareUrl ? 'Rendering the proof…' : 'Rendering the template…'}
              </span>
            </div>
          )}

          {rendered.error && (
            <div className="grid min-h-[300px] place-items-center px-6 text-center">
              <p className="[font-size:13px] text-[var(--pv-stop)]">{rendered.error}</p>
            </div>
          )}

          {repeats !== null && !isOverleaf && (
            <div className="absolute inset-0 z-[70] grid place-items-center bg-[var(--pv-paper-sunk)]/90 px-6 text-center">
              <div>
                <p className="[font-size:var(--pv-text-h3)] font-bold">Repeats page {repeats}</p>
                <p className="mx-auto mt-2 max-w-[42ch] [font-size:13px] leading-relaxed text-[var(--pv-muted)]">
                  This page is {thisKind ? `a ${PAGE_KIND_LABEL[thisKind].toLowerCase()}` : ''} too, so it gets
                  exactly the marks placed on page {repeats} — on a real certificate, every page of this kind
                  does. Edit them on page {repeats}; the Proof shows them here.
                </p>
              </div>
            </div>
          )}

          {isOverleaf && (
            <div className="absolute inset-0 z-[70] grid place-items-center bg-[var(--pv-paper-sunk)]/90 px-6 text-center">
              <div>
                <p className="[font-size:var(--pv-text-h3)] font-bold">Overleaf notes</p>
                <p className="mx-auto mt-2 max-w-[40ch] [font-size:13px] leading-relaxed text-[var(--pv-muted)]">
                  Nothing is printed on this page. Many FTA certificates carry their standard
                  conditions on the reverse, and a seal across them is wrong — the page is not
                  part of what the certificate asserts.
                </p>
              </div>
            </div>
          )}

          {!busy &&
            !rendered.error &&
            onPage.map((slot) => {
              const layout = layouts[slot.id ?? ''];
              if (!layout) return null;
              const rect = boxToScreen(layout.box, geometry);
              const covered = coveredIds.has(slot.id ?? '');
              return (
                <button
                  key={slot.id}
                  type="button"
                  onPointerDown={(e) => beginMove(e, slot)}
                  onKeyDown={(e) => onKeyDown(e, slot)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`${slot.label ?? slot.slotType}, ${Math.round(layout.box.width)} by ${Math.round(layout.box.height)} points at ${slot.x}, ${slot.y}. Drag or use arrow keys to move; ] and [ change its layer.`}
                  aria-pressed={selectedId === slot.id}
                  // Layer order IS the DOM order: later marks paint over earlier ones, as
                  // the engine draws them. No z-index on selection, deliberately.
                  style={rect}
                  className={cn(
                    'group absolute cursor-move touch-none rounded-[1px] outline-none',
                    compareUrl
                      ? 'border border-dashed border-[var(--pv-leaf-deep)]'
                      : covered
                        ? 'border border-dashed border-[var(--pv-warn)]'
                        : 'border border-transparent hover:border-dashed hover:border-[var(--pv-leaf-deep)]/70',
                  )}
                >
                  {!compareUrl && <MarkContent slot={slot} layout={layout} scale={geometry.scale} />}
                </button>
              );
            })}

          {/* The selection lives on its own layer above every mark, so a selected mark is
              visible and resizable wherever it sits in the stack — without moving it. */}
          {!busy && !rendered.error && selected && selectedLayout && (
            <SelectionFrame
              slot={selected}
              layout={selectedLayout}
              geometry={geometry}
              onResizeStart={(event, handle) => beginResize(event, selected, handle)}
            />
          )}
        </div>
      </div>

      {blank && (
        <div className="flex flex-wrap items-center gap-3 rounded-[var(--pv-radius)] border border-dashed border-[var(--pv-line-strong)] bg-[var(--pv-card-alt)] px-3.5 py-2.5">
          <Upload aria-hidden className="size-4 shrink-0 text-[var(--pv-muted)]" />
          <p className="min-w-[18ch] flex-1 [font-size:12.5px] leading-snug text-[var(--pv-muted)]">
            You are placing against a blank {Math.round(pointWidth)} × {Math.round(pointHeight)}pt
            page. Upload the empty form and the marks appear over the real boxes, which is the only
            way to see an overlap before a certificate carries it.
          </p>
          <Button variant="secondary" size="sm" onClick={onUploadTemplate} leadingIcon={<Upload className="size-3.5" />}>
            Upload the template
          </Button>
        </div>
      )}

      <p className="[font-size:var(--pv-text-2xs)] leading-snug text-[var(--pv-faint)]">
        Drag to move, drag a corner to resize. Arrow keys nudge by 1pt (Shift for 10); <kbd>]</kbd>{' '}
        and <kbd>[</kbd> bring the selected mark forward or send it back. Every mark is drawn at
        the size the engine prints it, in the order it prints — what overlaps here overlaps on the
        certificate. <span className="font-[family-name:var(--pv-font-mono)]">SAMPLE-000001</span>{' '}
        is a stand-in, not a real certificate number.
        {onPage.length === 0 && ' Nothing is placed on this page yet — add a mark to begin.'}
      </p>
    </div>
  );
}

const HANDLE_CURSORS: Record<ResizeHandle, string> = {
  nw: 'cursor-nwse-resize',
  se: 'cursor-nwse-resize',
  ne: 'cursor-nesw-resize',
  sw: 'cursor-nesw-resize',
  e: 'cursor-ew-resize',
  w: 'cursor-ew-resize',
};

function SelectionFrame({
  slot,
  layout,
  geometry,
  onResizeStart,
}: {
  slot: StampSlot;
  layout: MarkLayout;
  geometry: PageGeometry;
  onResizeStart: (event: React.PointerEvent, handle: ResizeHandle) => void;
}) {
  const rect = boxToScreen(layout.box, geometry);
  const image = slot.slotType === 'SEAL' || slot.slotType === 'SIGNATURE';
  const handles: ResizeHandle[] = image ? ['nw', 'ne', 'sw', 'se'] : ['w', 'e'];

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute z-50 outline outline-[1.5px] outline-dashed outline-[var(--pv-ink)] shadow-[0_0_0_3px_var(--pv-leaf)]"
      style={rect}
    >
      <span className="absolute -top-5 left-0 whitespace-nowrap rounded-[3px] bg-[var(--pv-brand)] px-1.5 py-px [font-size:10px] font-semibold text-[var(--pv-brand-on)]">
        {slot.label ?? slot.slotType} · {Math.round(layout.box.width)} × {Math.round(layout.box.height)} pt
      </span>
      {handles.map((handle) => (
        <span
          key={handle}
          onPointerDown={(event) => onResizeStart(event, handle)}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            'pointer-events-auto absolute size-2.5 touch-none rounded-[1px] border-[1.5px] border-[var(--pv-ink)] bg-[var(--pv-card)]',
            HANDLE_CURSORS[handle],
            handle.includes('n') && '-top-[5px]',
            handle.includes('s') && '-bottom-[5px]',
            handle.includes('w') && '-left-[5px]',
            handle.includes('e') && '-right-[5px]',
            (handle === 'e' || handle === 'w') && 'top-1/2 -translate-y-1/2',
          )}
        />
      ))}
    </div>
  );
}

export function SlotTypeBadge({ type }: { type: string }) {
  const tone =
    type === 'SEAL' || type === 'SIGNATURE' ? 'leaf' : type === 'CERTIFICATE_NUMBER' ? 'ink' : 'neutral';
  return (
    <Badge tone={tone as never} size="sm">
      {type.replace(/_/g, ' ').toLowerCase()}
    </Badge>
  );
}

/** The CSS font closest to each standard PDF font, for drawing — never for measuring. */
const CSS_FONT = {
  Helvetica: { fontFamily: 'Helvetica, Arial, sans-serif', fontWeight: 400 },
  'Helvetica-Bold': { fontFamily: 'Helvetica, Arial, sans-serif', fontWeight: 700 },
  Courier: { fontFamily: '"Courier New", Courier, monospace', fontWeight: 400 },
  'Times-Roman': { fontFamily: '"Times New Roman", Times, serif', fontWeight: 400 },
} as const;

/**
 * What a mark prints, drawn inside its box.
 *
 * The BOX is exact — it is the engine's own arithmetic. The glyphs inside a text mark are
 * the browser's rendering of the nearest system font, so their shapes can differ by a hair
 * from the PDF's; their width and position come from the engine's metrics.
 *
 * A mark that would print NOTHING — a seal slot with no seal chosen, a signature with none
 * on file — renders as a hatched warning rather than as empty space. Silent blankness is the
 * failure mode that reaches an issued certificate.
 */
function MarkContent({ slot, layout, scale }: { slot: StampSlot; layout: MarkLayout; scale: number }) {
  const missing = (label: string) => (
    <span
      className="flex size-full items-center justify-center rounded-[2px] border border-dashed border-[var(--pv-clay-deep)] bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,var(--pv-clay-soft)_4px,var(--pv-clay-soft)_8px)] px-1 text-center [font-size:9px] leading-tight text-[var(--pv-clay-deep)]"
      title={`${label} — nothing would print here`}
    >
      {label}
    </span>
  );

  if (slot.slotType === 'SEAL' || slot.slotType === 'SIGNATURE') {
    if (!layout.imageUrl) return missing(slot.slotType === 'SEAL' ? 'No seal chosen' : 'No signature on file');
    return (
      <img
        src={layout.imageUrl}
        alt=""
        draggable={false}
        className="pointer-events-none block size-full"
        style={{ opacity: slot.opacity }}
      />
    );
  }

  if (!layout.text) return missing('No text set');

  const font = CSS_FONT[markFontFor(slot.fontFamily)];
  return (
    <span
      className="pointer-events-none absolute top-0 block whitespace-nowrap"
      style={{
        left: ((layout.drawX ?? layout.box.x) - layout.box.x) * scale,
        // fontSize is in PDF points; the canvas is scaled, so the on-screen size is too.
        fontSize: slot.fontSize * scale,
        lineHeight: `${layout.box.height * scale}px`,
        color: slot.fontColor,
        opacity: slot.opacity,
        ...font,
      }}
    >
      {layout.text}
    </span>
  );
}
